import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { checkAuthStatus, logout } from '../api/auth';
import { useNavigate } from 'react-router-dom';

// Create the context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Function to check/refresh auth status
  const checkAuth = useCallback(async () => {
    try {
      const response = await checkAuthStatus();
      setIsAuthenticated(response.isAuthenticated);
      setUser(response.user);
      return response;
    } catch (error) {
      console.error('Error checking authentication:', error);
      setIsAuthenticated(false);
      setUser(null);
      return { isAuthenticated: false, user: null };
    } finally {
      setLoading(false);
    }
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      setIsAuthenticated(false);
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Value to be provided by the context
  const value = {
    user,
    isAuthenticated,
    loading,
    logout: handleLogout,
    refreshUser: checkAuth  // Expose function to refresh user data
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 